import { managedGroups, planPrintCart, isCompletePrintGroup } from './printocom-order.js';

const configNode = document.getElementById('pc-cart-config');
if (configNode) {
  const config = JSON.parse(configNode.textContent);
  let queue = Promise.resolve();
  let pending = 0;
  const permitted = new WeakSet();
  const locked = (task) => {
    pending++;
    const result = queue.then(task);
    queue = result.catch(() => {}).finally(() => { pending--; });
    return result;
  };
  const request = async (url, body) => {
    const endpoint = body && !url.endsWith('.js') ? url + '.js' : url;
    const response = await fetch(endpoint, body ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) } : { cache: 'no-store' });
    let cart;
    try { cart = await response.json(); } catch { throw new Error(config.messages.error); }
    if (!response.ok || cart.errors || cart.status) throw new Error(config.messages.error);
    return cart;
  };
  const read = () => request(routes.cart_url + '.js');
  const sectionsBody = (sections) => ({ sections, sections_url: window.location.pathname });
  const repair = async (initial, sections = [], group = '') => {
    let cart = initial;
    let plan = planPrintCart(cart, config.allowed, group);
    if (plan.invalid) throw new Error(config.messages.invalid);
    let changed = false;
    if (Object.keys(plan.updates).length) {
      cart = await request(routes.cart_update_url, { updates: plan.updates, ...sectionsBody(sections) });
      changed = true;
    }
    plan = planPrintCart(cart, config.allowed, group);
    if (plan.invalid) throw new Error(config.messages.invalid);
    if (plan.missing.length) {
      await request(routes.cart_add_url, { items: plan.missing });
      cart = await request(routes.cart_update_url, { updates: {}, ...sectionsBody(sections) });
      changed = true;
    }
    plan = planPrintCart(cart, config.allowed, group);
    if (plan.invalid || Object.keys(plan.updates).length || plan.missing.length) throw new Error(config.messages.error);
    return { cart, changed };
  };
  const status = (message) => {
    const node = document.querySelector('[data-pc-cart-status]');
    if (node) {
      node.textContent = message;
      node.hidden = !message;
    } else {
      const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
      if (errors) errors.textContent = message;
    }
  };
  const hasLegacy = (cart) => cart.items.some(item => config.handles.includes(item.handle) && item.properties?._pc_customizer !== '1');

  window.PrintocomCart = {
    get busy() { return pending > 0; },
    change: (key, quantity, sections) => locked(async () => {
      if (!Number.isSafeInteger(quantity) || quantity < 0) throw new Error(config.messages.error);
      const before = await read();
      const parent = before.items.find(item => item.key === key);
      if (!parent || parent.properties?._pc_customizer !== '1') throw new Error(config.messages.error);
      if (parent.properties._pc_role !== 'garment') throw new Error(config.messages.child);
      const group = parent.properties._pc_group;
      let cart = await request(routes.cart_change_url, { id: key, quantity, ...sectionsBody(sections) });
      if (!quantity) {
        const remaining = managedGroups(cart).get(group) || [];
        if (remaining.length) cart = await request(routes.cart_update_url, { updates: Object.fromEntries(remaining.map(item => [item.key, 0])), ...sectionsBody(sections) });
        return cart;
      }
      const result = await repair(cart, sections, group);
      if (!isCompletePrintGroup(result.cart, group, config.allowed)) throw new Error(config.messages.error);
      return result.cart;
    }),
  };

  const checkout = async (button) => {
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    status(config.messages.checking);
    try {
      await queue;
      const cart = await read();
      if (hasLegacy(cart)) throw new Error(config.messages.legacy);
      const result = await locked(() => repair(cart));
      if (result.changed) {
        window.location.href = routes.cart_url + '?pc_review=1';
        return;
      }
      if (!result.cart.items.length) throw new Error(config.messages.error);
      status('');
      permitted.add(button);
      button.form.requestSubmit(button);
    } catch (error) {
      status(error.message || config.messages.error);
    } finally {
      button.removeAttribute('aria-busy');
    }
  };
  document.addEventListener('click', (event) => {
    const button = event.target.closest('button[name="checkout"]');
    if (!button || button.disabled || permitted.has(button)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (pending) return status(config.messages.updating);
    checkout(button);
  }, true);
  document.addEventListener('submit', (event) => {
    const button = event.submitter;
    if (button?.name !== 'checkout' || permitted.has(button)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (pending) return status(config.messages.updating);
    checkout(button);
  }, true);
  const init = () => {
    document.querySelectorAll('[data-pc-checkout]').forEach(button => { button.disabled = false; });
    if (new URLSearchParams(window.location.search).has('pc_review')) status(config.messages.review);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
}
